import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { QNA_MAX_IMAGE_LENGTH } from '@repo/constants';
import { ImagePreviewModal } from './ImagePreview.modal';
import { downloadFileAs, isImageFile } from '@repo/utils';
import { ExpiringImage } from '../common/ExpiringImage';

type Props = {
  content: string;
  createdDate: string;
  isQuestion: boolean;
  imageUrls: string[];
  /** 첨부 URL 이 만료됐을 때 목록을 다시 받아오도록 알린다. */
  onImageExpired?: () => void;
};

export const ChatBox = ({
  content,
  createdDate,
  isQuestion,
  imageUrls,
  onImageExpired,
}: Props) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (textRef?.current) {
      setHeight(textRef.current.scrollHeight);
    }
  }, []);

  const formatDate = (date: string) => {
    return date.split('T').join(' ');
  };

  return (
    <Wrapper isQuestion={isQuestion}>
      <Date isQuestion={isQuestion}>{formatDate(createdDate)}</Date>
      <Text
        ref={textRef}
        name=''
        id=''
        value={content}
        disabled
        height={height}
        isQuestion={isQuestion}
      />
      {imageUrls && imageUrls.length > 0 && (
        <ImageList>
          {imageUrls.map((imageUrl) => {
            if (!isImageFile(imageUrl)) {
              return (
                // 서명 URL 은 cross-origin 이라 <a download> 의 파일명 지정이
                // 무시된다. blob 으로 받아서 저장한다.
                <PDFDownloadLink
                  key={imageUrl}
                  type='button'
                  onClick={() => {
                    downloadFileAs(imageUrl).catch(() => {
                      alert('파일 다운로드에 실패했습니다.');
                    });
                  }}
                >
                  PDF
                </PDFDownloadLink>
              );
            }
            return (
              <Button key={imageUrl} onClick={() => setPreviewUrl(imageUrl)}>
                <Image src={imageUrl} alt='' onExpired={onImageExpired} />
              </Button>
            );
          })}
          {Array.from(
            { length: QNA_MAX_IMAGE_LENGTH - (imageUrls?.length || 0) },
            (_, index) => (
              <BlankImageSection key={index} aria-hidden></BlankImageSection>
            ),
          )}
        </ImageList>
      )}
      {previewUrl && (
        <ImagePreviewModal
          imgUrl={previewUrl}
          onTogglePreview={() => setPreviewUrl(null)}
          onClose={() => setPreviewUrl(null)}
          onExpired={onImageExpired}
        />
      )}
    </Wrapper>
  );
};

type WrapperStyleProps = {
  isQuestion: boolean;
};

type TextStyleProps = {
  height: number;
};

const Wrapper = styled.div<WrapperStyleProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 1px solid #e5e6e8;
  border-radius: 8px;
  max-width: 90%;
  padding: 14px 16px;
  background-color: ${({ isQuestion, theme }) =>
    isQuestion ? theme.ktpPrimary100 : '#fff'};
`;

const Date = styled.small<WrapperStyleProps>`
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 8px;
  color: ${({ isQuestion }) => (isQuestion ? '#fff' : '#030303')};
`;

const Text = styled.textarea<TextStyleProps & WrapperStyleProps>`
  resize: none;
  border: none;
  height: ${({ height }) => height}px;
  line-height: 150%;
  font-size: 14px;
  font-weight: 500;
  color: ${({ isQuestion }) => (isQuestion ? '#fff' : '#030303')};
  &:disabled {
    opacity: 1;
  }
  background-color: ${({ theme, isQuestion }) =>
    isQuestion ? theme.ktpPrimary100 : '#fff'};
`;

const ImageList = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  aspect-ratio: 268/64;
  width: 100%;
  overflow-x: auto;
  margin-top: 24px;
`;

const BlankImageSection = styled.div`
  height: 100%;
  aspect-ratio: 1/1;
  overflow: hidden;
  border-radius: 6px;
`;

const Button = styled.button`
  height: 100%;
  aspect-ratio: 1/1;
  overflow: hidden;
  border-radius: 6px;
`;

const Image = styled(ExpiringImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PDFDownloadLink = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  height: 100%;
  aspect-ratio: 1/1;
  border-radius: 8px;
  border: 1px dashed #e5e6e8;
  color: #111;
  background-color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.24px;
  padding: 0;
  font-family: inherit;
  cursor: pointer;
`;
